/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { exchangeScreensFlow } from '../../constants';
import ModalRoot, { Modal, ModalBody, ModalHeader } from '../../../../components/Modal';
import Exchange from './exchange';
import ConfirmExchange from './confirmExchange';
import { useLedgerFilteredWallets } from '../../../../utilities/sharedHooks/ledgerWallets';
import { WALLETS_ALL } from '../../queries';
import Currencies from './currencies';
import { walletTypes } from '../../../../utilities/constants';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { RATES_QUERY_KEY } from '../../../../utilities/sharedQueries';

const ExchangeModal = ({
  hide,
  wallet,
  fiatCurrencyCode,
  supportedCurrencies,
  forcedDestinationWallet,
  isUserAccountPage,
}) => {
  const [currentScreen, setCurrentScreen] = useState(exchangeScreensFlow.MAIN);
  const [modalHasInit, setModalHasInit] = useState(false);

  const { data: wallets } = useLedgerFilteredWallets(WALLETS_ALL);
  const { data: rates } = useQuery(RATES_QUERY_KEY);

  const [allowCryptoToCryptoExchange] = useSetting(
    'app_features',
    'feature_allow_crypto_to_crypto_exchange'
  );

  const [form, updateForm] = useState({
    source: wallet ? supportedCurrencies[wallet?.coin] : null,
    destination: null,
    sourceAmount: 0,
    destinationAmount: 0,
    sourceWallet: wallet || null,
    destinationWallet: null,
    isOpenSourceCurrencies: false,
    checkOpenForExchange: false,
  });

  const userWalletKeys = useMemo(() => {
    let _result = [];
    if (wallets && wallets.length > 0) {
      _result = wallets.map((w) => w.coin);
    }

    return _result;
  }, [wallets]);

  const fromCurrenciesKeys = useMemo(() => {
    let keys = [];
    for (let i = 0; i < Object.entries(supportedCurrencies).length; i += 1) {
      const [key, value] = Object.entries(supportedCurrencies)[i];
      if (value?.currency_code?.toLowerCase() !== form?.destination?.currency_code?.toLowerCase()) {
        if (
          (value.allows_sell_exchange === true || value.exchange_for_crypto === true) &&
          userWalletKeys.includes(key) === true
        ) {
          keys.push(value);
        }
      }
    }

    if (forcedDestinationWallet && rates) {
      keys = keys.filter((key) => {
        const rateKey = `${forcedDestinationWallet?.coin?.toUpperCase()}${key?.currency_display_name?.toUpperCase()}`;
        const currentRate = rates[rateKey];
        return currentRate ? currentRate && currentRate?.availableOnExchange : false;
      });
    }

    return keys.sort();
  }, [userWalletKeys, supportedCurrencies, form.destination, rates]);

  const toCurrenciesKeys = useMemo(() => {
    const keys = [];

    if (!forcedDestinationWallet) {
      for (let i = 0; i < Object.entries(supportedCurrencies).length; i += 1) {
        // eslint-disable-next-line no-unused-vars
        const [key, value] = Object.entries(supportedCurrencies)[i];
        if (
          rates[`${form?.source?.currency_code?.toUpperCase()}${key.toUpperCase()}`]
            ?.availableOnExchange
        ) {
          if (form?.source?.currency_type === walletTypes.FIAT) {
            if (
              (value.currency_type === walletTypes.CRYPTO ||
                value.currency_type === walletTypes.INTERNAL) &&
              value.allows_buy_exchange === true &&
              (value.can_create_wallet || userWalletKeys.includes(key) === true)
            ) {
              keys.push(value);
            }
          } else if (
            form?.source?.currency_type === walletTypes.CRYPTO ||
            form?.source?.currency_type === walletTypes.INTERNAL
          ) {
            if (
              !allowCryptoToCryptoExchange &&
              value.currency_type !== walletTypes.CRYPTO &&
              value.currency_type !== walletTypes.INTERNAL &&
              (value.can_create_wallet || userWalletKeys.includes(key) === true)
            ) {
              keys.push(value);
            } else if (allowCryptoToCryptoExchange) {
              if (
                value.currency_type === walletTypes.FIAT &&
                (value.can_create_wallet || userWalletKeys.includes(key) === true)
              ) {
                keys.push(value);
              }

              if (
                (value.currency_type === walletTypes.CRYPTO ||
                  value.currency_type === walletTypes.INTERNAL) &&
                value.currency_code !== form.source.currency_code &&
                value.exchange_for_crypto === true &&
                (value.can_create_wallet === true || userWalletKeys.includes(key) === true)
              ) {
                keys.push(value);
              }
            }
          }
        }
      }
    }

    return keys.sort();
  }, [supportedCurrencies, form.source, userWalletKeys, allowCryptoToCryptoExchange]);

  const DEFAULT_DESTINATION_CURRENCY = 'btc';

  useEffect(() => {
    if (!wallet && forcedDestinationWallet && form?.destination) {
      const _sourceWallet = wallets.find((w) => w.coin === fromCurrenciesKeys[0]?.currency_code);

      updateForm({
        ...form,
        source: fromCurrenciesKeys[0],
        sourceWallet: _sourceWallet,
      });
    }
  }, [fromCurrenciesKeys, form?.destination, wallet, forcedDestinationWallet]);

  useEffect(() => {
    if (!modalHasInit) {
      if (form.source && toCurrenciesKeys && toCurrenciesKeys.length > 0) {
        const _destinationCurrency =
          toCurrenciesKeys.find((c) => c.currency_code === DEFAULT_DESTINATION_CURRENCY) ||
          toCurrenciesKeys[0];

        const _destinationWallet = wallets.find(
          (w) => w.coin === _destinationCurrency.currency_code
        );

        updateForm({
          ...form,
          destination: _destinationCurrency,
          destinationWallet: _destinationWallet || null,
        });
      }
      setModalHasInit(true);
    }
  }, [form.source, toCurrenciesKeys, wallets, forcedDestinationWallet]);

  useEffect(() => {
    if (forcedDestinationWallet) {
      const _destinationCurrency = supportedCurrencies[forcedDestinationWallet?.coin];

      updateForm({
        ...form,
        destination: _destinationCurrency,
        destinationWallet: forcedDestinationWallet,
      });
    }
  }, []);

  const exchangeFlow = useMemo(
    () => [
      {
        screen: exchangeScreensFlow.MAIN,
        component: (
          <Exchange
            openScreen={setCurrentScreen}
            form={form}
            updateForm={updateForm}
            wallets={wallets}
            supportedCurrencies={supportedCurrencies}
            forcedDestinationWallet={forcedDestinationWallet}
            fromCurrenciesKeys={fromCurrenciesKeys}
            toCurrenciesKeys={toCurrenciesKeys}
          />
        ),
      },
      {
        screen: exchangeScreensFlow.CONFIRM_EXCHANGE,
        component: (
          <ConfirmExchange
            openScreen={setCurrentScreen}
            form={form}
            hide={hide}
            forcedDestinationWallet={forcedDestinationWallet}
            isUserAccountPage={isUserAccountPage}
          />
        ),
      },
      {
        screen: exchangeScreensFlow.CURRENCIES,
        component: (
          <Currencies
            wallets={wallets}
            currencies={form.isOpenSourceCurrencies ? fromCurrenciesKeys : toCurrenciesKeys}
            openScreen={setCurrentScreen}
            form={form}
            updateForm={updateForm}
            mainScreen={exchangeScreensFlow.MAIN}
            isOpenSourceCurrencies={form.isOpenSourceCurrencies}
          />
        ),
      },
    ],
    [
      fiatCurrencyCode,
      form,
      wallets,
      hide,
      fromCurrenciesKeys,
      toCurrenciesKeys,
      supportedCurrencies,
    ]
  );

  const selectedComponent = useMemo(
    () => exchangeFlow.find((item) => item.screen === currentScreen).component,
    [currentScreen, exchangeFlow]
  );

  return (
    <ModalRoot>
      <Modal>
        <ModalHeader>
          <i
            onClick={hide}
            onKeyDown={hide}
            role="button"
            className="close-btn cursor-pointer icon icon-close-other text-shadow"
            tabIndex={-1}
            aria-label="Close"
          />
        </ModalHeader>
        <ModalBody>{selectedComponent}</ModalBody>
      </Modal>
    </ModalRoot>
  );
};

export default ExchangeModal;

ExchangeModal.propTypes = {
  hide: PropTypes.func.isRequired,
  wallet: PropTypes.object.isRequired,
  fiatCurrencyCode: PropTypes.string.isRequired,
  supportedCurrencies: PropTypes.object.isRequired,
  forcedDestinationWallet: PropTypes.object,
  isUserAccountPage: PropTypes.bool,
};

ExchangeModal.defaultProps = {
  forcedDestinationWallet: null,
  isUserAccountPage: false,
};
